import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { Layers3 } from 'lucide-react';
import { lessonApi, type LessonHighlightsResponse } from '@/lib/api-client';
import type { StepView } from '@/types/pipeline';
import { HighlightsConceptGrid } from '@/components/features/learning/content/highlights/HighlightsConceptGrid';
import { HighlightsReader } from './HighlightsReader';
import { HighlightsEmptyState } from './HighlightsEmptyState';
import { HighlightsSkeleton } from '../../skeletons/PanelSkeletons';
import { StepGate } from '../../common/StepGate';
import {
  useGenerateContent,
  useRetryContent,
} from '@/hooks/lesson/useOnDemandContent';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useConceptTime } from '@/hooks/useConceptTime';
import { queryKeys } from '@/config/query-client';

/**
 * Highlights carousel - Inshorts/Stories-style full-viewport reader for
 * the lesson's per-concept carousel cards.
 *
 *  Two navigation axes:
 *    - Horizontal (within a concept): tap left/right halves, ←/→ keys,
 *      or chevron buttons. Clamps at the concept's first/last card -
 *      doesn't spill into the next concept.
 *    - Vertical (between concepts): swipe up/down or ↑/↓ keys. Resets
 *      the card index to 0 when concept changes.
 *
 *  Generation flow is unchanged: the parent's `OnDemandWrapper` gates the
 *  Generate / Loading / Failed states; only the `completed` branch renders
 *  the new layout.
 */

export interface LessonHighlightsCarouselProps {
  lessonId: string;
  /** Per-step view map from the page poll; the gate reads `steps.highlights`. */
  steps?: Record<string, StepView>;
  /** Polling cadence in ms while in-flight; defaults to 4s. */
  pollIntervalMs?: number;
  /** Off-flow waiting-vs-generate split. */
  lessonDone?: boolean;
  /** True once metadata + first pipeline-status snapshot loaded. */
  lessonReady?: boolean;
}

export function LessonHighlightsCarousel({
  lessonId,
  steps,
  pollIntervalMs,
  lessonDone,
  lessonReady,
}: LessonHighlightsCarouselProps) {
  const interval = pollIntervalMs ?? 4000;

  // Mount/unmount activity tracking for the Highlights sub-tab.
  useActivityTracking({ contentType: 'highlights', lessonId });
  const setVisibleConcepts = useConceptTime(lessonId);

  const view = steps?.highlights;
  const state = view?.state;

  // Fetch the artifact only once the step is in flight or done; suppress while
  // not_started so we don't 404 a not-yet-generated artifact. Poll while the
  // step is still processing to pick up the finished payload.
  const stepActive =
    state === 'queued' || state === 'processing' || state === 'completed';

  const { data, isLoading } = useQuery<LessonHighlightsResponse>({
    queryKey: queryKeys.lessons.highlights(lessonId),
    queryFn: () => lessonApi.getHighlights(lessonId),
    refetchInterval: query => {
      const d = query.state.data as LessonHighlightsResponse | undefined;
      const docInflight = d?.status === 'pending' || d?.status === 'processing';
      const stepInflight = state === 'queued' || state === 'processing';
      return docInflight || stepInflight ? interval : false;
    },
    staleTime: 0,
    enabled: !!lessonId && (stepActive || lessonDone === true),
  });

  const generateContent = useGenerateContent(lessonId);
  const retryContent = useRetryContent(lessonId);

  // Step is `completed` (so StepGate would render children), but the content
  // may not be ready: either the first fetch is still in flight (`isLoading`),
  // or a poll caught the step flipping to completed while the highlights doc
  // itself is still `processing` (data present but carousels empty). Show the
  // skeleton in both cases instead of flashing the empty state.
  const contentPending =
    isLoading || data?.status === 'pending' || data?.status === 'processing';
  if (contentPending && state === 'completed') {
    return <HighlightsSkeleton />;
  }

  const carousels = data?.carousels || [];

  return (
    <StepGate
      step='highlights'
      lessonId={lessonId}
      steps={steps}
      lessonDone={lessonDone}
      lessonReady={lessonReady}
      skeleton={<HighlightsSkeleton />}
      isGenerating={
        generateContent.pendingType === 'highlights' ||
        retryContent.pendingType === 'highlights'
      }
      generate={{
        icon: Layers3,
        label: 'highlights',
        description:
          'Generate short, swipeable cards summarising each concept.',
        onGenerate: () => generateContent.mutate('highlights'),
      }}
      onRetry={() => retryContent.mutate('highlights')}
    >
      {carousels.length === 0 ? (
        <HighlightsEmptyState />
      ) : (
        <HighlightsContent
          carousels={carousels}
          onConceptShown={setVisibleConcepts}
        />
      )}
    </StepGate>
  );
}

// ---------------------------------------------------------------------------
// Content: concept overview grid + full-screen reader overlay
// ---------------------------------------------------------------------------

function HighlightsContent({
  carousels,
  onConceptShown,
}: {
  carousels: LessonHighlightsResponse['carousels'];
  /** The concept on screen in the reader, null when closed. */
  onConceptShown?: (conceptIds: string[]) => void;
}) {
  const [activeConcept, setActiveConcept] = useState<number | null>(null);
  const showConcept = useCallback(
    (id: string | null) => onConceptShown?.(id ? [id] : []),
    [onConceptShown]
  );

  return (
    <>
      <HighlightsConceptGrid carousels={carousels} onOpen={setActiveConcept} />

      {activeConcept !== null && (
        // A full-screen reader over the lesson is a modal dialog: named, and
        // marked modal so assistive tech (and the phone checks) treat the
        // page under it as out of reach.
        <div
          role='dialog'
          aria-modal='true'
          aria-label='Highlights reader'
          className='fixed inset-0 z-50 bg-background motion-safe:animate-fade-in'
        >
          <HighlightsReader
            carousels={carousels}
            initialConceptIdx={activeConcept}
            onConceptChange={showConcept}
            onClose={() => {
              showConcept(null);
              setActiveConcept(null);
            }}
          />
        </div>
      )}
    </>
  );
}
